import { useState } from "react";
import { api } from "@/lib/api";
import SEO from "@/components/SEO";
import { toast } from "sonner";
import { Mail, MapPin, Phone } from "lucide-react";

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", { ...f, source_page: window.location.pathname });
      toast.success("Thanks — we will get back to you within 24 hours.");
      setF({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(detail || "Could not send. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fade-in" data-testid="page-contact">
      <SEO title="Contact us" description="Get in touch with the CreatorMic Pro team." path="/contact" />
      <section className="max-w-6xl mx-auto px-6 md:px-10 pt-16 md:pt-24 pb-20 grid md:grid-cols-2 gap-14">
        <div>
          <p className="eyebrow mb-4">Contact</p>
          <h1 className="text-h1 text-[#1D1D1F]">Let’s talk.</h1>
          <p className="mt-6 text-lg text-[#86868B] max-w-md">Real humans, fast replies. Support hours 9am–6pm CT, Monday to Friday.</p>

          <div className="mt-10 space-y-5">
            <div className="flex gap-4 items-start">
              <Mail size={20} strokeWidth={1.5} className="text-[#0066FF] mt-1" />
              <div>
                <p className="text-sm font-semibold text-[#1D1D1F]">Support email</p>
                <a href="mailto:support@creatormicpro.com" className="text-[#86868B] hover:text-[#0066FF]">support@creatormicpro.com</a>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <Phone size={20} strokeWidth={1.5} className="text-[#0066FF] mt-1" />
              <div>
                <p className="text-sm font-semibold text-[#1D1D1F]">Support phone</p>
                <p className="text-[#86868B]">Coming soon — please email for the fastest response.</p>
              </div>
            </div>
            <div className="flex gap-4 items-start">
              <MapPin size={20} strokeWidth={1.5} className="text-[#0066FF] mt-1" />
              <div>
                <p className="text-sm font-semibold text-[#1D1D1F]">Business</p>
                <p className="text-[#86868B]">CreatorMic Pro, operated by CLSX LLC</p>
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="bg-[#F5F5F7] rounded-3xl p-8 md:p-10 space-y-4" data-testid="contact-form">
          <div>
            <label className="text-sm font-medium">Your name</label>
            <input required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="contact-name" className="mt-2 w-full h-12 px-4 rounded-xl bg-white border border-transparent focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] focus:outline-none" />
          </div>
          <div>
            <label className="text-sm font-medium">Email</label>
            <input required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="contact-email" className="mt-2 w-full h-12 px-4 rounded-xl bg-white border border-transparent focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] focus:outline-none" />
          </div>
          <div>
            <label className="text-sm font-medium">Subject</label>
            <input value={f.subject} onChange={(e) => setF({ ...f, subject: e.target.value })} data-testid="contact-subject" className="mt-2 w-full h-12 px-4 rounded-xl bg-white border border-transparent focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] focus:outline-none" />
          </div>
          <div>
            <label className="text-sm font-medium">Message</label>
            <textarea required rows={5} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" className="mt-2 w-full p-4 rounded-xl bg-white border border-transparent focus:border-[#0066FF] focus:ring-1 focus:ring-[#0066FF] focus:outline-none resize-none" />
          </div>
          <button type="submit" disabled={loading} className="btn-primary w-full" data-testid="contact-submit">
            {loading ? "Sending..." : "Send message"}
          </button>
        </form>
      </section>
    </div>
  );
}
